import type { CustomerSummary, ResourceAvailability } from '@stayafrica/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CheckboxField,
  Container,
  FormField,
  IconButton,
  Input,
  NativeSelect,
  NoPermissionState,
  Notice,
  PageHeader,
  SegmentedSwitch,
  Skeleton,
  Textarea,
  toast,
} from '@stayafrica/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Ban,
  Building2,
  CalendarDays,
  Check,
  Clock,
  Layers,
  Percent,
  Trash2,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan, useSession } from '@/features/auth/session';
import { CustomerFormDrawer } from '@/features/customers/CustomerFormDrawer';
import { CustomerSearchField } from '@/features/customers/CustomerSearchField';
import { customerQueryOptions } from '@/features/customers/queries';
import { DepositDrawer } from '@/features/reservations/DepositDrawer';
import { QuoteSummary } from '@/features/reservations/QuoteSummary';
import { ReservationErrorNotice } from '@/features/reservations/ReservationErrorNotice';
import {
  addDaysIso,
  nightsBetween,
  optionExpiryDefault,
  toDateInput,
  toDateTimeInput,
  toIso,
} from '@/features/reservations/datetime';
import {
  confirmReservation,
  createReservation,
  quoteQueryOptions,
  recordDeposit,
  type CreateReservationPayload,
  type QuoteLineInput,
  type QuotePayload,
} from '@/features/reservations/queries';
import {
  DEFAULT_CHECKIN_TIME,
  DEFAULT_CHECKOUT_TIME,
  DEFAULT_OPTION_HOURS,
} from '@/features/reservations/status';
import { availabilityQueryOptions, ratePlansQueryOptions } from '@/features/resources/queries';
import { optionalWithholding, taxRulesQueryOptions } from '@/features/tax/queries';
import { formatAmount, formatPercent } from '@/lib/format';

interface NewReservationSearch {
  resourceId?: string;
  from?: string;
  to?: string;
  guestId?: string;
  billingPartyId?: string;
}

export const Route = createFileRoute('/_app/reservations/new')({
  validateSearch: (search: Record<string, unknown>): NewReservationSearch => ({
    ...(typeof search['resourceId'] === 'string' ? { resourceId: search['resourceId'] } : {}),
    ...(typeof search['from'] === 'string' ? { from: search['from'] } : {}),
    ...(typeof search['to'] === 'string' ? { to: search['to'] } : {}),
    ...(typeof search['guestId'] === 'string' ? { guestId: search['guestId'] } : {}),
    ...(typeof search['billingPartyId'] === 'string'
      ? { billingPartyId: search['billingPartyId'] }
      : {}),
  }),
  component: NewReservationPage,
});

type BillingMode = 'GUEST' | 'COMPANY';

interface ExtraLine extends QuoteLineInput {
  key: string;
}

/** Extras proposés en un clic (prototype 04, bloc 3). */
const EXTRA_PRESETS = [
  { key: 'BREAKFAST', unitPrice: 5_000 },
  { key: 'AIRPORT_SHUTTLE', unitPrice: 15_000 },
  { key: 'EXTRA_CLEANING', unitPrice: 10_000 },
  { key: 'LAUNDRY', unitPrice: 12_000 },
] as const;

/**
 * Nouvelle réservation — page en trois blocs et résumé collant, fidèle au
 * prototype validé `prototype/04-nouvelle-reservation.html` :
 * 1. Séjour (dates + heures 12 h 00 par défaut, ressource proposée par
 *    `GET /availability`, sous-ressources sous leur parent) ;
 * 2. Client (recherche instantanée, « Facturé à » pré-rempli avec l'entreprise
 *    principale, création inline) ;
 * 3. Tarif & extras (plan tarifaire, prix sous permission, remise, retenue 5 %).
 */
function NewReservationPage() {
  const { t, i18n } = useTranslation([
    'reservations',
    'customers',
    'resources',
    'common',
    'errors',
  ]);
  const search = Route.useSearch();
  const navigate = useNavigate();
  const session = useSession();
  const canCreate = useCan('reservation.create');
  const canOverridePrice = useCan('reservation.override_price');
  const canHold = useCan('reservation.hold');
  const canConfirm = useCan('reservation.confirm');
  const canConfirmWithoutDeposit = useCan('reservation.confirm.without_deposit');
  const canRecordPayment = useCan('payment.record');
  const canReadTax = useCan('tax.read');

  /* ------------------------------ Bloc 1 : séjour ------------------------ */
  const today = toDateInput(new Date());
  const [arrivalDate, setArrivalDate] = useState(search.from ? toDateInput(search.from) : today);
  const [arrivalTime, setArrivalTime] = useState(DEFAULT_CHECKIN_TIME);
  const [departureDate, setDepartureDate] = useState(
    search.to ? toDateInput(search.to) : addDaysIso(today, 1),
  );
  const [departureTime, setDepartureTime] = useState(DEFAULT_CHECKOUT_TIME);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [earlyCheckin, setEarlyCheckin] = useState(false);
  const [resourceId, setResourceId] = useState<string | undefined>(search.resourceId);

  /* ------------------------------ Bloc 2 : client ------------------------ */
  const [guest, setGuest] = useState<CustomerSummary | null>(null);
  const [billingMode, setBillingMode] = useState<BillingMode>('GUEST');
  const [billingCompany, setBillingCompany] = useState<CustomerSummary | null>(null);
  const [customerDrawer, setCustomerDrawer] = useState<{
    open: boolean;
    kind?: 'PERSON' | 'COMPANY';
    term?: string;
  }>({ open: false });

  /* ------------------------------ Bloc 3 : tarif ------------------------- */
  const [ratePlanId, setRatePlanId] = useState<string | null>(null);
  const [unitPrice, setUnitPrice] = useState<string>('');
  const [discountMode, setDiscountMode] = useState<'PERCENT' | 'AMOUNT'>('PERCENT');
  const [discountValue, setDiscountValue] = useState('');
  const [extras, setExtras] = useState<ExtraLine[]>([]);
  const [applyWithholding, setApplyWithholding] = useState(false);
  const [notes, setNotes] = useState('');

  /* ------------------------------ Sorties -------------------------------- */
  const [optionExpiresAt, setOptionExpiresAt] = useState(() =>
    toDateTimeInput(optionExpiryDefault(DEFAULT_OPTION_HOURS)),
  );
  const [optionOpen, setOptionOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [withoutDepositOpen, setWithoutDepositOpen] = useState(false);
  const [withoutDepositReason, setWithoutDepositReason] = useState('');
  const [submitError, setSubmitError] = useState<unknown>(null);

  const arrivalAt = toIso(arrivalDate, arrivalTime);
  const departureAt = toIso(departureDate, departureTime);
  const nights = nightsBetween(arrivalDate, departureDate);
  const datesValid = new Date(departureAt).getTime() > new Date(arrivalAt).getTime();

  /* ------------------------------ Requêtes ------------------------------- */
  const availabilityQuery = useQuery({
    ...availabilityQueryOptions(
      { from: arrivalAt, to: departureAt, capacity: adults + children },
      datesValid && canCreate,
    ),
  });
  const ratePlansQuery = useQuery({
    ...ratePlansQueryOptions(resourceId ?? ''),
    enabled: Boolean(resourceId),
  });
  const presetGuestQuery = useQuery({
    ...customerQueryOptions(search.guestId ?? ''),
    enabled: Boolean(search.guestId),
  });
  const presetBillingQuery = useQuery({
    ...customerQueryOptions(search.billingPartyId ?? ''),
    enabled: Boolean(search.billingPartyId),
  });
  // Le taux et l'existence même de la retenue viennent du paramétrage serveur
  // (`GET /tax-rules`) : l'écran ne code pas les 5 % en dur.
  const taxRulesQuery = useQuery(taxRulesQueryOptions({ kind: 'WITHHOLDING' }, canReadTax));
  const withholdingRule = optionalWithholding(taxRulesQuery.data);

  // Pré-remplissage depuis une fiche client (`?guestId` / `?billingPartyId`).
  useEffect(() => {
    const preset = presetGuestQuery.data;
    if (!preset || guest) return;
    setGuest({
      id: preset.id,
      kind: preset.kind,
      displayName: preset.displayName,
      email: preset.email,
      phonePrimary: preset.phonePrimary,
      primaryCompany: preset.memberships.find(
        (link) => link.personId === preset.id && link.isPrimary,
      )
        ? {
            id: preset.memberships.find((link) => link.isPrimary)!.companyId,
            displayName: preset.memberships.find((link) => link.isPrimary)!.companyName,
          }
        : null,
    });
  }, [presetGuestQuery.data, guest]);

  useEffect(() => {
    const preset = presetBillingQuery.data;
    if (!preset || billingCompany) return;
    setBillingMode('COMPANY');
    setBillingCompany({
      id: preset.id,
      kind: preset.kind,
      displayName: preset.displayName,
      email: preset.email,
      phonePrimary: preset.phonePrimary,
      primaryCompany: null,
    });
  }, [presetBillingQuery.data, billingCompany]);

  // « Facturé à » pré-rempli avec l'entreprise principale de la personne (D-005).
  useEffect(() => {
    if (!guest?.primaryCompany) return;
    setBillingMode('COMPANY');
    setBillingCompany({
      id: guest.primaryCompany.id,
      kind: 'COMPANY',
      displayName: guest.primaryCompany.displayName,
      email: null,
      phonePrimary: null,
      primaryCompany: null,
    });
  }, [guest]);

  const availability = availabilityQuery.data ?? [];
  const selected = availability.find((item) => item.resourceId === resourceId);
  const ratePlans = (ratePlansQuery.data ?? []).filter((plan) => plan.isActive);

  // Le plan par défaut de la ressource s'applique dès qu'elle change.
  useEffect(() => {
    if (ratePlans.length === 0) return;
    if (ratePlanId && ratePlans.some((plan) => plan.id === ratePlanId)) return;
    const fallback = ratePlans.find((plan) => plan.isDefault) ?? ratePlans[0];
    if (fallback) {
      setRatePlanId(fallback.id);
      setUnitPrice(String(fallback.price));
    }
  }, [ratePlans, ratePlanId]);

  // La retenue à la source est proposée quand le facturé est une entreprise (D-012).
  const billingParty = billingMode === 'COMPANY' ? billingCompany : guest;
  const billingIsCompany = billingParty?.kind === 'COMPANY';
  useEffect(() => {
    setApplyWithholding(billingIsCompany);
  }, [billingIsCompany]);

  /* ------------------------------ Devis ---------------------------------- */
  const quotePayload: QuotePayload | null =
    resourceId && datesValid
      ? {
          resourceId,
          arrivalAt,
          departureAt,
          ...(billingParty ? { billingPartyId: billingParty.id } : {}),
          ratePlanId,
          ...(unitPrice !== '' ? { unitPrice: Number(unitPrice.replace(/\s/g, '')) } : {}),
          discountType: discountValue ? discountMode : 'NONE',
          discountValue: Number(discountValue.replace(/\s/g, '')) || 0,
          lines: extras.map(({ kind, label, quantity, unitPrice: price }) => ({
            kind,
            label,
            quantity,
            unitPrice: price,
          })),
          // Sans règle de retenue applicable, le champ n'est pas envoyé : le
          // serveur retient alors le défaut de l'entreprise facturée (D-012).
          ...(withholdingRule ? { applyWithholding } : {}),
        }
      : null;

  const [debouncedQuote, setDebouncedQuote] = useState<QuotePayload | null>(null);
  useEffect(() => {
    const serialised = JSON.stringify(quotePayload);
    const timer = window.setTimeout(
      () => setDebouncedQuote(serialised ? (JSON.parse(serialised) as QuotePayload) : null),
      300,
    );
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la sérialisation évite les re-rendus inutiles
  }, [JSON.stringify(quotePayload)]);

  const quoteQuery = useQuery(quoteQueryOptions(debouncedQuote));
  const quote = quoteQuery.data;

  /* ------------------------------ Création -------------------------------- */
  const buildPayload = (
    initialStatus: CreateReservationPayload['initialStatus'],
  ): CreateReservationPayload | null => {
    if (!resourceId || !guest || !quotePayload) return null;
    return {
      ...quotePayload,
      guestId: guest.id,
      ...(billingParty ? { billingPartyId: billingParty.id } : {}),
      adults,
      children,
      earlyCheckinRequested: earlyCheckin,
      notesInternal: notes.trim() || null,
      initialStatus,
      ...(initialStatus === 'OPTION'
        ? { optionExpiresAt: new Date(optionExpiresAt).toISOString() }
        : {}),
    };
  };

  const createMutation = useMutation({
    mutationFn: async (input: {
      status: CreateReservationPayload['initialStatus'];
      confirm?: { deposit?: Parameters<typeof recordDeposit>[1]; withoutDepositReason?: string };
    }) => {
      const payload = buildPayload(input.status);
      if (!payload) throw new Error(t('reservations:create.incomplete'));
      // `initialStatus: 'OPTION'` pose déjà le verrou et l'expiration côté serveur.
      const created = await createReservation(payload);
      if (input.confirm) {
        let current = created;
        if (input.confirm.deposit) {
          await recordDeposit(created.id, input.confirm.deposit);
          current = { ...created, version: created.version };
        }
        return confirmReservation(current.id, current.version, input.confirm.withoutDepositReason);
      }
      return created;
    },
    onSuccess: async (created) => {
      setSubmitError(null);
      setDepositOpen(false);
      setOptionOpen(false);
      setWithoutDepositOpen(false);
      toast.success(t('reservations:toasts.created', { reference: created.reference }));
      await navigate({
        to: '/reservations/$reservationId',
        params: { reservationId: created.id },
      });
    },
    onError: (error) => setSubmitError(error),
  });

  if (!canCreate) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const ready = Boolean(resourceId && guest && datesValid);
  const capacityExceeded = selected ? adults + children > selected.capacityMax : false;
  const net = quote?.totals.net ?? 0;

  /* --------- Ressources : sous-ressources affichées sous leur parent ------- */
  const parents = availability.filter((item) => !item.parentResourceId);
  const ordered: ResourceAvailability[] = parents.flatMap((parent) => [
    parent,
    ...availability.filter((item) => item.parentResourceId === parent.resourceId),
  ]);
  const orphans = availability.filter(
    (item) =>
      item.parentResourceId &&
      !parents.some((parent) => parent.resourceId === item.parentResourceId),
  );
  const resourceCards = [...ordered, ...orphans];
  const availableCount = resourceCards.filter((item) => item.available).length;

  return (
    <Container width="narrow">
      <PageHeader
        title={t('reservations:create.title')}
        subtitle={t('reservations:create.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('reservations:title'),
            link: <Link to="/reservations">{t('reservations:title')}</Link>,
          },
          { label: t('reservations:create.breadcrumb') },
        ]}
        actions={
          <Button variant="ghost" asChild>
            <Link to="/reservations">{t('common:actions.cancel')}</Link>
          </Button>
        }
      />

      <div className="grid grid--main-aside">
        <div className="stack stack--lg">
          {submitError ? <ReservationErrorNotice error={submitError} /> : null}

          {/* ----------------------- Bloc 1 : Séjour ----------------------- */}
          <Card id="bloc-sejour">
            <CardHeader
              title={t('reservations:create.step1')}
              icon={<CalendarDays className="ico ico-16" aria-hidden="true" />}
            />
            <CardBody>
              <div className="field-row-3 mb-5">
                <FormField
                  label={t('reservations:fields.arrivalDate')}
                  required
                  requiredLabel={t('common:fields.required')}
                  hint={t('reservations:create.checkinHint')}
                >
                  {(field) => (
                    <Input
                      id={field.id}
                      type="date"
                      value={arrivalDate}
                      onChange={(event) => setArrivalDate(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField label={t('reservations:fields.arrivalTime')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      type="time"
                      value={arrivalTime}
                      onChange={(event) => setArrivalTime(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField label={t('reservations:fields.nights')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      readOnly
                      value={t('reservations:nightsCount', { count: nights })}
                      style={{ background: 'var(--surface-3)', fontWeight: 600 }}
                    />
                  )}
                </FormField>
              </div>

              <div className="field-row-3 mb-5">
                <FormField
                  label={t('reservations:fields.departureDate')}
                  required
                  requiredLabel={t('common:fields.required')}
                  hint={t('reservations:create.checkoutHint')}
                  error={!datesValid ? t('reservations:create.datesInvalid') : undefined}
                >
                  {(field) => (
                    <Input
                      id={field.id}
                      type="date"
                      invalid={field.invalid}
                      value={departureDate}
                      onChange={(event) => setDepartureDate(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField label={t('reservations:fields.departureTime')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      type="time"
                      value={departureTime}
                      onChange={(event) => setDepartureTime(event.target.value)}
                    />
                  )}
                </FormField>
                <div />
              </div>

              <div className="field-row mb-4">
                <FormField label={t('reservations:fields.adults')}>
                  {(field) => (
                    <NativeSelect
                      id={field.id}
                      value={String(adults)}
                      onChange={(event) => setAdults(Number(event.target.value))}
                    >
                      {[1, 2, 3, 4, 5, 6].map((value) => (
                        <option key={value} value={value}>
                          {t('reservations:adultsCount', { count: value })}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </FormField>
                <FormField label={t('reservations:fields.children')}>
                  {(field) => (
                    <NativeSelect
                      id={field.id}
                      value={String(children)}
                      onChange={(event) => setChildren(Number(event.target.value))}
                    >
                      {[0, 1, 2, 3].map((value) => (
                        <option key={value} value={value}>
                          {value === 0
                            ? t('reservations:childrenNone')
                            : t('reservations:childrenCount', { count: value })}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </FormField>
              </div>

              <CheckboxField
                label={t('reservations:fields.earlyCheckin')}
                description={t('reservations:create.earlyCheckinHint')}
                checked={earlyCheckin}
                onCheckedChange={(checked) => setEarlyCheckin(checked === true)}
              />

              {capacityExceeded ? (
                <Notice tone="danger" className="mt-4" role="alert">
                  {t('reservations:create.capacityExceeded', {
                    name: selected?.resourceName,
                    max: selected?.capacityMax,
                  })}
                </Notice>
              ) : null}

              <fieldset className="mt-5">
                <legend className="label mb-3">
                  {t('reservations:create.resourceLegend')}
                  <span className="req" aria-hidden="true">
                    *
                  </span>
                </legend>

                {availabilityQuery.isPending ? (
                  <div className="stack stack--sm" aria-busy="true">
                    <Skeleton height={72} />
                    <Skeleton height={72} />
                    <Skeleton height={72} />
                  </div>
                ) : availabilityQuery.isError ? (
                  <ReservationErrorNotice error={availabilityQuery.error} />
                ) : (
                  <>
                    <Notice tone="info" className="mb-4">
                      <p className="mb-0 t-13">
                        {t('reservations:create.availabilityCount', {
                          count: availableCount,
                          people: adults + children,
                        })}
                      </p>
                    </Notice>
                    <div className="radio-cards">
                      {resourceCards.map((item) => (
                        <ResourceRadioCard
                          key={item.resourceId}
                          item={item}
                          checked={resourceId === item.resourceId}
                          onSelect={() => setResourceId(item.resourceId)}
                        />
                      ))}
                    </div>
                    <Notice
                      tone="violet"
                      className="mt-3"
                      icon={<Layers className="ico ico-16 notice__ico" aria-hidden="true" />}
                    >
                      <p className="mb-0 t-12">{t('reservations:create.compositeNotice')}</p>
                    </Notice>
                  </>
                )}
              </fieldset>
            </CardBody>
          </Card>

          {/* ----------------------- Bloc 2 : Client ----------------------- */}
          <Card id="bloc-client">
            <CardHeader
              title={t('reservations:create.step2')}
              icon={<User className="ico ico-16" aria-hidden="true" />}
              iconTone="green"
            />
            <CardBody>
              <CustomerSearchField
                label={t('reservations:fields.guest')}
                hint={t('reservations:create.guestHint')}
                value={guest}
                onChange={setGuest}
                kind="PERSON"
                required
                placeholder={t('customers:search.personPlaceholder')}
                createLabel={t('customers:search.createPerson')}
                onCreate={(term) => setCustomerDrawer({ open: true, kind: 'PERSON', term })}
              />

              <hr />

              <fieldset className="mt-4">
                <legend className="label mb-2">
                  {t('reservations:fields.billingParty')}
                  <span className="req" aria-hidden="true">
                    *
                  </span>
                </legend>
                <p className="hint mb-3">{t('reservations:create.billingHint')}</p>
                <SegmentedSwitch<BillingMode>
                  label={t('reservations:fields.billingParty')}
                  value={billingMode}
                  onValueChange={setBillingMode}
                  className="mb-4"
                  options={[
                    {
                      value: 'GUEST',
                      label: t('reservations:create.billingGuest'),
                      icon: <User className="ico ico-16" aria-hidden="true" />,
                    },
                    {
                      value: 'COMPANY',
                      label: t('reservations:create.billingCompany'),
                      icon: <Building2 className="ico ico-16" aria-hidden="true" />,
                    },
                  ]}
                />

                {billingMode === 'GUEST' ? (
                  <Notice tone="info">
                    <p className="mb-0 t-13">
                      {guest
                        ? t('reservations:create.billingGuestNotice', { name: guest.displayName })
                        : t('reservations:create.billingGuestEmpty')}
                    </p>
                  </Notice>
                ) : (
                  <CustomerSearchField
                    label={t('reservations:fields.billingCompany')}
                    value={billingCompany}
                    onChange={setBillingCompany}
                    kind="COMPANY"
                    placeholder={t('customers:search.companyPlaceholder')}
                    createLabel={t('customers:search.createCompany')}
                    onCreate={(term) => setCustomerDrawer({ open: true, kind: 'COMPANY', term })}
                  />
                )}
              </fieldset>
            </CardBody>
          </Card>

          {/* ----------------------- Bloc 3 : Tarif ------------------------ */}
          <Card id="bloc-tarif">
            <CardHeader
              title={t('reservations:create.step3')}
              icon={<Percent className="ico ico-16" aria-hidden="true" />}
              iconTone="amber"
            />
            <CardBody>
              <div className="field-row-3 mb-5">
                <FormField label={t('reservations:fields.ratePlan')}>
                  {(field) => (
                    <NativeSelect
                      id={field.id}
                      value={ratePlanId ?? ''}
                      disabled={ratePlans.length === 0}
                      onChange={(event) => {
                        const plan = ratePlans.find((item) => item.id === event.target.value);
                        setRatePlanId(event.target.value || null);
                        if (plan) setUnitPrice(String(plan.price));
                      }}
                    >
                      {ratePlans.length === 0 ? (
                        <option value="">{t('reservations:create.noRatePlan')}</option>
                      ) : null}
                      {ratePlans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} — {formatAmount(plan.price, i18n.language)}{' '}
                          {t(`resources:rates.perUnit.${plan.pricingUnit}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </FormField>

                <FormField
                  label={t('reservations:fields.unitPrice')}
                  hint={
                    canOverridePrice
                      ? t('reservations:create.priceOverrideHint')
                      : t('reservations:create.priceLockedHint')
                  }
                >
                  {(field) => (
                    <Input
                      id={field.id}
                      suffix="XOF"
                      inputMode="numeric"
                      readOnly={!canOverridePrice}
                      value={unitPrice}
                      aria-readonly={!canOverridePrice}
                      onChange={(event) => setUnitPrice(event.target.value)}
                      style={canOverridePrice ? undefined : { background: 'var(--surface-3)' }}
                    />
                  )}
                </FormField>

                <FormField label={t('reservations:fields.quantity')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      readOnly
                      value={String(quote?.quantity ?? nights)}
                      style={{ background: 'var(--surface-3)' }}
                    />
                  )}
                </FormField>
              </div>

              <div className="field-row mb-5">
                <FormField
                  label={t('reservations:fields.discount')}
                  hint={
                    quote && quote.totals.discountTotal > 0
                      ? t('reservations:create.discountHint', {
                          amount: formatAmount(quote.totals.discountTotal, i18n.language),
                        })
                      : undefined
                  }
                >
                  {(field) => (
                    <div className="row" style={{ gap: 8 }}>
                      <Input
                        id={field.id}
                        inputMode="numeric"
                        suffix={discountMode === 'PERCENT' ? '%' : 'XOF'}
                        value={discountValue}
                        onChange={(event) => setDiscountValue(event.target.value)}
                        style={{ flex: 1 }}
                      />
                      <SegmentedSwitch<'PERCENT' | 'AMOUNT'>
                        label={t('reservations:fields.discountMode')}
                        value={discountMode}
                        onValueChange={setDiscountMode}
                        options={[
                          { value: 'PERCENT', label: '%' },
                          { value: 'AMOUNT', label: 'XOF' },
                        ]}
                      />
                    </div>
                  )}
                </FormField>
                <div />
              </div>

              <fieldset className="mb-5">
                <legend className="label mb-3">{t('reservations:fields.extras')}</legend>
                <div className="grid grid-2" style={{ gap: 10 }}>
                  {EXTRA_PRESETS.map((preset) => {
                    const active = extras.some((line) => line.key === preset.key);
                    return (
                      <label
                        key={preset.key}
                        className="check card card--flat"
                        style={{ padding: '12px 14px' }}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={(event) =>
                            setExtras((current) =>
                              event.target.checked
                                ? [
                                    ...current,
                                    {
                                      key: preset.key,
                                      // `kind` est obligatoire côté contrat.
                                      kind: 'EXTRA' as const,
                                      label: t(`reservations:extras.${preset.key}`),
                                      quantity: 1,
                                      unitPrice: preset.unitPrice,
                                    },
                                  ]
                                : current.filter((line) => line.key !== preset.key),
                            )
                          }
                        />
                        <span style={{ flex: 1 }}>
                          <span className="strong">{t(`reservations:extras.${preset.key}`)}</span>
                        </span>
                        <span className="amount t-13">
                          {formatAmount(preset.unitPrice, i18n.language)}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {extras.length > 0 ? (
                  <div className="stack stack--sm mt-3">
                    {extras.map((line, index) => (
                      <div className="row" key={line.key} style={{ gap: 8 }}>
                        <span className="t-13" style={{ flex: 1 }}>
                          {line.label}
                        </span>
                        <Input
                          aria-label={t('reservations:fields.extraQuantity', { label: line.label })}
                          inputMode="numeric"
                          value={String(line.quantity)}
                          onChange={(event) =>
                            setExtras((current) =>
                              current.map((item, position) =>
                                position === index
                                  ? { ...item, quantity: Number(event.target.value) || 1 }
                                  : item,
                              ),
                            )
                          }
                          style={{ width: 72 }}
                        />
                        <Input
                          aria-label={t('reservations:fields.extraPrice', { label: line.label })}
                          inputMode="numeric"
                          suffix="XOF"
                          value={String(line.unitPrice)}
                          onChange={(event) =>
                            setExtras((current) =>
                              current.map((item, position) =>
                                position === index
                                  ? { ...item, unitPrice: Number(event.target.value) || 0 }
                                  : item,
                              ),
                            )
                          }
                          style={{ width: 140 }}
                        />
                        <IconButton
                          label={t('reservations:create.removeExtra', { label: line.label })}
                          onClick={() =>
                            setExtras((current) => current.filter((item) => item.key !== line.key))
                          }
                        >
                          <Trash2 className="ico ico-16" aria-hidden="true" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                ) : null}
              </fieldset>

              {billingIsCompany && withholdingRule ? (
                <Notice
                  tone="violet"
                  className="mb-5"
                  icon={<Percent className="ico ico-16 notice__ico" aria-hidden="true" />}
                >
                  <CheckboxField
                    label={t('reservations:fields.applyWithholding', {
                      rate: formatPercent(withholdingRule.rate, i18n.language),
                    })}
                    description={t('reservations:create.withholdingHint')}
                    checked={applyWithholding}
                    onCheckedChange={(checked) => setApplyWithholding(checked === true)}
                  />
                </Notice>
              ) : null}

              <FormField label={t('reservations:fields.notesInternal')}>
                {(field) => (
                  <Textarea
                    id={field.id}
                    rows={3}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder={t('reservations:create.notesPlaceholder')}
                  />
                )}
              </FormField>
            </CardBody>
          </Card>
        </div>

        {/* ----------------------- Résumé collant ------------------------- */}
        <aside className="sticky-aside">
          <Card variant="accent">
            <CardHeader
              title={t('reservations:summary.title')}
              actions={<Badge tone="neutral">{t('reservations:status.DRAFT')}</Badge>}
            />
            <CardBody density="tight">
              <QuoteSummary
                quote={quote}
                loading={quoteQuery.isPending}
                resourceName={selected?.resourceName}
                propertyName={session.user.properties[0]?.name}
                arrivalAt={arrivalAt}
                departureAt={departureAt}
                nights={nights}
                adults={adults}
                children={children}
                guest={guest}
                billingParty={billingParty}
              />

              <div className="stack stack--sm mt-4">
                <Button
                  variant="primary"
                  block
                  disabled={!ready || !canConfirm}
                  onClick={() => setDepositOpen(true)}
                >
                  <Check className="ico ico-16" aria-hidden="true" />
                  {t('reservations:create.confirmAction')}
                </Button>
                <Button
                  variant="secondary"
                  block
                  disabled={!ready || !canHold}
                  onClick={() => setOptionOpen(true)}
                >
                  <Clock className="ico ico-16" aria-hidden="true" />
                  {t('reservations:create.holdAction')}
                </Button>
                <Button
                  variant="ghost"
                  block
                  disabled={!ready}
                  loading={createMutation.isPending && !depositOpen && !optionOpen}
                  onClick={() => createMutation.mutate({ status: 'DRAFT' })}
                >
                  {t('reservations:create.draftAction')}
                </Button>
              </div>
            </CardBody>
          </Card>
        </aside>
      </div>

      {/* Tiroir « Poser une option » : date d'expiration (+48 h par défaut). */}
      {optionOpen ? (
        <Card className="mt-5" id="option-panel">
          <CardHeader
            title={t('reservations:create.holdTitle')}
            icon={<Clock className="ico ico-16" aria-hidden="true" />}
            iconTone="amber"
          />
          <CardBody>
            <FormField
              label={t('reservations:fields.optionExpiresAt')}
              hint={t('reservations:create.holdHint')}
            >
              {(field) => (
                <Input
                  id={field.id}
                  type="datetime-local"
                  value={optionExpiresAt}
                  onChange={(event) => setOptionExpiresAt(event.target.value)}
                />
              )}
            </FormField>
            <div className="row mt-4" style={{ gap: 8 }}>
              <Button variant="ghost" onClick={() => setOptionOpen(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={createMutation.isPending}
                onClick={() => createMutation.mutate({ status: 'OPTION' })}
              >
                {t('reservations:create.holdSubmit')}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <DepositDrawer
        open={depositOpen}
        onOpenChange={setDepositOpen}
        net={net}
        balance={net}
        title={t('reservations:create.depositTitle')}
        description={t('reservations:create.depositSubtitle')}
        submitLabel={t('reservations:create.depositSubmit')}
        loading={createMutation.isPending}
        {...(!canRecordPayment
          ? { errorMessage: t('reservations:create.depositNoPermission') }
          : {})}
        onSubmit={(values) =>
          createMutation.mutate({ status: 'DRAFT', confirm: { deposit: values } })
        }
        secondaryAction={
          canConfirmWithoutDeposit ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setDepositOpen(false);
                setWithoutDepositOpen(true);
              }}
            >
              <Ban className="ico ico-16" aria-hidden="true" />
              {t('reservations:create.withoutDepositAction')}
            </Button>
          ) : null
        }
      />

      {withoutDepositOpen ? (
        <Card className="mt-5">
          <CardHeader
            title={t('reservations:create.withoutDepositTitle')}
            icon={<Ban className="ico ico-16" aria-hidden="true" />}
            iconTone="amber"
          />
          <CardBody>
            <Notice tone="warning" className="mb-4">
              <p className="mb-0 t-13">
                {t('reservations:create.withoutDepositWarning', {
                  amount: formatAmount(net, i18n.language),
                })}
              </p>
            </Notice>
            <FormField
              label={t('reservations:create.withoutDepositReason')}
              required
              requiredLabel={t('common:fields.required')}
              hint={t('reservations:create.withoutDepositReasonHint')}
            >
              {(field) => (
                <Textarea
                  id={field.id}
                  rows={3}
                  value={withoutDepositReason}
                  onChange={(event) => setWithoutDepositReason(event.target.value)}
                />
              )}
            </FormField>
            <div className="row mt-4" style={{ gap: 8 }}>
              <Button variant="ghost" onClick={() => setWithoutDepositOpen(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={withoutDepositReason.trim().length < 10}
                loading={createMutation.isPending}
                onClick={() =>
                  createMutation.mutate({
                    status: 'DRAFT',
                    confirm: { withoutDepositReason: withoutDepositReason.trim() },
                  })
                }
              >
                {t('reservations:create.withoutDepositSubmit')}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <CustomerFormDrawer
        open={customerDrawer.open}
        onOpenChange={(open) => setCustomerDrawer((current) => ({ ...current, open }))}
        {...(customerDrawer.kind ? { lockedKind: customerDrawer.kind } : {})}
        {...(customerDrawer.term ? { initialTerm: customerDrawer.term } : {})}
        onCreated={(created) => {
          const summary: CustomerSummary = {
            id: created.id,
            kind: created.kind,
            displayName: created.displayName,
            email: created.email,
            phonePrimary: created.phonePrimary,
            primaryCompany: null,
          };
          if (created.kind === 'PERSON') setGuest(summary);
          else {
            setBillingMode('COMPANY');
            setBillingCompany(summary);
          }
        }}
      />
    </Container>
  );
}

/** Carte de ressource proposée : prix, capacité, disponibilité, sous-ressource. */
function ResourceRadioCard({
  item,
  checked,
  onSelect,
}: {
  item: ResourceAvailability;
  checked: boolean;
  onSelect: () => void;
}) {
  const { t, i18n } = useTranslation(['reservations', 'resources', 'common']);
  const rate = item.defaultRate;
  return (
    <label
      className={[
        'radio-card',
        item.parentResourceId ? 'is-sub' : '',
        item.available ? '' : 'is-disabled',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        type="radio"
        name="reservation-resource"
        checked={checked}
        disabled={!item.available}
        onChange={onSelect}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="row row--between" style={{ gap: 12 }}>
          <span className="radio-card__title">{item.resourceName}</span>
          {rate ? (
            <span className="amount">
              {formatAmount(rate.price, i18n.language, rate.currencyCode)}
              <span className="cur">{t(`resources:rates.perUnit.${rate.pricingUnit}`)}</span>
            </span>
          ) : null}
        </span>
        <span className="radio-card__desc">
          {t('reservations:create.capacityLabel', { count: item.capacityMax })}
          {item.parentResourceId ? ` · ${t('reservations:create.subResource')}` : ''}
        </span>
        <span className="row mt-2" style={{ gap: 6 }}>
          {item.available ? (
            <Badge tone="green">
              <Check className="ico ico-14" aria-hidden="true" />
              {t('reservations:create.available')}
            </Badge>
          ) : (
            <Badge tone="neutral">
              <Ban className="ico ico-14" aria-hidden="true" />
              {t('reservations:create.unavailable')}
            </Badge>
          )}
          {item.parentResourceId ? (
            <Badge tone="violet">
              <Layers className="ico ico-14" aria-hidden="true" />
              {t('reservations:create.subResourceBadge')}
            </Badge>
          ) : null}
          {!item.available && item.conflicts[0] ? (
            <span className="t-12 muted">
              {t('reservations:create.conflictWith', { name: item.conflicts[0].resourceName })}
            </span>
          ) : null}
        </span>
      </span>
    </label>
  );
}
